import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { Header, Image, Menu, MenuItemProps } from 'semantic-ui-react'
import close_modal from '../../../../assets/svg/close_modal.svg'
import withSuspense from '../../../../components/HOC/SuspenseHOC'
import TransitionModal from '../../../../components/Modal/TransitionModal'
import ValidationInput from '../../../../components/ValidationInput/ValidationInput'
import { useWindowSize } from '../../../../hooks/useWindowSize'
import { RootState } from '../../../../store/rootState'
import { IUserDetail } from '../../../../types/user'
import { checkRole } from '../../../../utils/checkRole'
import * as EmployeesActions from '../../../settings/actions/employeeActions'
import { IEmployee } from '../../../settings/types/employee'
import * as TimeTrackingActions from '../../../time-tracking/actions/timeTrackingActions'
import { ITimeTracking } from '../../../time-tracking/types/timeTracking'
import * as CostsActions from '../../actions/directAndPassTroughCosts'
import ActivityDetailsLogs from '../../components/activity-details/ActivityDetailsLogs'
import PassTroughDetailsOverviewComponent from '../../components/activity-details/PassTroughCostDetailsOverview'
import { _initialPassTroughCost } from '../../reducers/directAndPassTroughCostsReducer'
import { ICostHistory, IPassThroughCost } from '../../types/directAndPassTroughCosts'
import { IProject } from '../../types/project'
import { IQueryActivityEmployee } from '../../types/projectActivity'

interface StateProps {
  selectedCost: IPassThroughCost
  project: IProject
  employees: IEmployee[]
  timeTrackingEntries: ITimeTracking[]
  openCostDetailsModal: boolean
  user: IUserDetail | null
  history: ICostHistory[]
  employee: IEmployee | undefined
}

interface DispatchProps {
  updatePTCost: (projectId: number | string, cost: IPassThroughCost) => Promise<IPassThroughCost>
  getPTCost: (projectId: number | string, cost: IPassThroughCost) => Promise<IPassThroughCost>
  setSelectedCost: (cost: IPassThroughCost) => void
  getEmployees: (queryParams?: IQueryActivityEmployee) => void
  getPTCostHistory: (projectId: number | string, cost: IPassThroughCost, searchQuery?: string) => void
  setOpenDetailsModal: (open: boolean) => void
}

type Props = StateProps & DispatchProps

const PassTroughDetailsContainer = (props: Props): JSX.Element => {
  const {
    project,
    selectedCost,
    employees,
    timeTrackingEntries,
    openCostDetailsModal,
    user,
    history,
    updatePTCost,
    getPTCost,
    setSelectedCost,
    getEmployees,
    setOpenDetailsModal,
    getPTCostHistory
  } = props

  const _tabs = [
    { name: 'Overview', key: 'overview', rights: [] },
    { name: 'Logs', key: 'logs', rights: [] }
  ]
  const [activeTab, setActiveTab] = useState(_tabs[0].key)
  const [costName, setCostName] = useState('')
  const [_employees, setEmployees] = useState<IEmployee[]>([])

  const windowHeight = useWindowSize().height

  useEffect(() => {
    if (project.permissions === 'restricted') {
      getEmployees({ projects: project.id.toString() })
    } else {
      getEmployees()
    }
  }, [getEmployees, project, selectedCost])

  useEffect(() => {
    if (project.time_tracking_perm.task_permission === 'all_prj_tasks') {
      const empIds = timeTrackingEntries.flatMap(e => +e.employee.id)
      const _emps = employees.filter(e => empIds.includes(+e.id))
      setEmployees(_emps.length > 0 ? _emps : [])
    } else {
      setEmployees(employees)
    }
  }, [employees, project, timeTrackingEntries, selectedCost])

  useEffect(() => {
    setCostName(selectedCost.offer_position)
  }, [selectedCost])

  const handleTabClick = (data: MenuItemProps) => {
    if (data.name) {
      setActiveTab(data.name.toLowerCase())
    }
  }

  const updateCost = (projectId: number | string, cost_update: IPassThroughCost, openDatepicker: boolean) => {
    if (!openDatepicker) {
      updatePTCost(projectId, cost_update).then(() => {
        getPTCost(projectId, cost_update).then(
          resp => {
            setSelectedCost(resp)
            setCostName(resp.offer_position)
          })
      })
    }
  }

  const inputChange = (event: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLTextAreaElement>) => {
    setCostName(event.target.value)
  }

  const inputOnBlur = (event: React.FocusEvent<HTMLInputElement> | React.FocusEvent<HTMLTextAreaElement>) => {
    const value = event.target.value
    if (value) {
      updateCost(project.id, { ...selectedCost, offer_position: value }, false)
      setCostName(value)
    }
  }

  const renderHeader = () => {
    return (
      <Header style={{ marginBottom: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {activeTab === 'overview' ?
          <ValidationInput
            fluid
            placeholder='Enter task name'
            fieldWidthPercentage={'100%'}
            className={'act-name-input'}
            value={costName}
            onChange={e => inputChange(e)}
            onBlur={e => inputOnBlur(e)}
            symbolPadding={.3}
            transparent={true}
            disabled={true}
          /> : <Header.Content style={{ float: 'left', color: '#172533', letterSpacing: '.25px', fontSize: '.2.25rem' }}>{costName}</Header.Content>}
      </Header>
    )
  }

  const filterHistory = (searchQuery: string) => {
    getPTCostHistory(project.id, selectedCost as IPassThroughCost, searchQuery)
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <PassTroughDetailsOverviewComponent
            cost={selectedCost}
            project={project}
            employees={_employees}
            updateCost={updateCost}
            costName={costName}
            user={user}
          />
        )

      case 'logs':
        return <ActivityDetailsLogs history={history ? history : []} filterHistory={filterHistory} />

      default:
        return null
    }
  }

  const renderTabs = () => {
    return (
      <Menu widths={3} pointing secondary style={{ margin: '32px 0 16px' }}>
        {_tabs.map(tab => {
          if (user && checkRole(user, tab.rights)) {
            return (
              <Menu.Item
                style={{
                  color: activeTab === tab.key ? '#08D6A1' : '#172533',
                  fontWeight: activeTab === tab.key ? 'bold' : '#172533',
                  fontSize: '18px',
                  letterSpacing: '0',
                  lineHeight: '21px',
                  paddingTop: '0',
                  paddingBottom: '18px'
                }}
                key={tab.name}
                name={tab.name}
                active={activeTab === tab.key}
                onClick={(e, data) => handleTabClick(data)}
              />
            )
          }
        })}
      </Menu>
    )
  }

  const closeModal = () => {
    setSelectedCost(_initialPassTroughCost)
    setOpenDetailsModal(false)
  }

  const renderContent = () => {
    return (
      <div style={{
        position: 'fixed',
        height: windowHeight,
        width: '39rem',
        background: '#F9FBFC',
        top: 0,
        right: 0,
        boxShadow: '0 0 10px -5px #151515',
        borderRadius: '8px',
        zIndex: 999
      }}>
        <Image id='closeModal' src={close_modal} onClick={closeModal} style={{ margin: '6%', cursor: 'pointer' }} />
        <div style={{ display: 'flex', flexDirection: 'column', margin: '0% 6% 0' }}>
          {renderHeader()}
          {renderTabs()}
        </div>
        {renderTabContent()}
      </div>
    )
  }

  return (
    <React.Fragment>
      <TransitionModal
        visible={openCostDetailsModal}
        animation='slide left'
        duration={{ show: 500, hide: 500 }}
        children={renderContent()}
        clickOutside={closeModal}
      />
    </React.Fragment>
  )
}

const mapStateToProps = (state: RootState): StateProps => {
  return {
    selectedCost: state.projectsState.selectedCost as IPassThroughCost,
    project: state.projectsState.project,
    employees: state.userSettingsState.employees,
    timeTrackingEntries: state.timeTrackingState.timeTrackingEntries,
    openCostDetailsModal: state.projectsState.openCostDetailsModal,
    employee: state.authState.user?.employee,
    history: state.projectsState.costHistory,
    user: state.authState.user
  }
}

const mapDispatchToProps = (
  dispatch: CostsActions.DirectAndPassTroughCostThunkDispatchType & EmployeesActions.EmployeeThunkDispatchType & TimeTrackingActions.TimeTrackingThunkDispatchType
): DispatchProps => {
  return {
    updatePTCost: async (projectId, cost) => dispatch(CostsActions.updatePassTroughCost(projectId, cost)),
    getPTCost: (projectId, cost) => dispatch(CostsActions.getPassTroughCost(projectId, cost)),
    setSelectedCost: cost => dispatch(CostsActions.setSelectedCost(cost)),
    getEmployees: (queryParams?) => dispatch(EmployeesActions.getEmployees(queryParams)),
    getPTCostHistory: (projectId, cost, search) => dispatch(CostsActions.getPassTroughCostHistory(projectId, cost, search)),
    setOpenDetailsModal: (open) => dispatch(CostsActions.setOpenCostDetailsModal(open)),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(withSuspense(PassTroughDetailsContainer))
